import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { UploadSimple, X } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function CookbookEditDialog({ cookbook, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (cookbook) setForm({
      title: cookbook.title || "",
      description: cookbook.description || "",
      cover_image_url: cookbook.cover_image_url || null,
      visibility: cookbook.privacy === "public" ? "public" : "private",
      notify: true,
    });
  }, [cookbook]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("cover_image_url", data.url);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    const wasPublic = cookbook.privacy === "public";
    const privacy = form.visibility === "public" ? "public" : (wasPublic ? "all_pages_shared" : cookbook.privacy);
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/cookbooks/${cookbook.cookbook_id}`, {
        title: form.title, description: form.description, cover_image_url: form.cover_image_url, privacy, notify: form.notify,
      });
      const rp = data.recipes_made_private ? ` · ${data.recipes_made_private} recipe${data.recipes_made_private === 1 ? "" : "s"} made private` : "";
      toast.success(data.changed?.length ? `Saved${rp}${form.notify ? " — owner emailed" : ""}` : "No changes");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!cookbook} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="admin-cookbook-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit cookbook</DialogTitle>
          <DialogDescription>Owner: {cookbook?.owner_email || "—"}</DialogDescription>
        </DialogHeader>
        {form && (
          <div className="space-y-4">
            <div>
              <label className="overline">Title</label>
              <input data-testid="admin-cookbook-edit-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="input-rf mt-1" />
            </div>
            <div>
              <label className="overline">Description</label>
              <textarea data-testid="admin-cookbook-edit-description" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className="input-rf mt-1" />
            </div>
            <div>
              <label className="overline">Cover image</label>
              <div className="mt-1 flex items-center gap-3">
                {form.cover_image_url && <img src={form.cover_image_url} alt="Cover" data-testid="admin-cookbook-edit-cover-preview" className="w-16 h-20 object-cover rounded-md border border-rf-ink/10" />}
                <button type="button" data-testid="admin-cookbook-edit-cover-upload" onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-outline-ink !py-2 !px-3 text-xs inline-flex items-center gap-1">
                  <UploadSimple size={14} /> {uploading ? "Uploading…" : (form.cover_image_url ? "Replace" : "Upload")}
                </button>
                {form.cover_image_url && (
                  <button type="button" data-testid="admin-cookbook-edit-cover-remove" onClick={() => set("cover_image_url", null)} className="text-xs font-bold text-rf-ink-soft hover:text-red-600 inline-flex items-center gap-1"><X size={12} /> Remove</button>
                )}
                <input ref={fileRef} type="file" accept="image/*" hidden data-testid="admin-cookbook-edit-cover-input" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
              </div>
            </div>
            <div>
              <label className="overline">Visibility</label>
              <select data-testid="admin-cookbook-edit-visibility" value={form.visibility} onChange={(e) => set("visibility", e.target.value)} className="input-rf mt-1">
                <option value="public">Public — listed in the directory</option>
                <option value="private">Private — owner and invitees only</option>
              </select>
              {cookbook.privacy === "public" && form.visibility === "private" && (
                <p className="text-xs text-rf-ink-soft mt-1.5" data-testid="admin-cookbook-edit-private-note">Every public recipe in this cookbook will also be made private and removed from the directory.</p>
              )}
              {cookbook.privacy !== "public" && form.visibility === "public" && (
                <p className="text-xs text-rf-ink-soft mt-1.5" data-testid="admin-cookbook-edit-public-note">The cookbook will be listed publicly. Its recipes keep their own privacy — the owner can make them all public from the cookbook page.</p>
              )}
            </div>
            <label className="flex items-center gap-2 text-sm text-rf-ink">
              <input type="checkbox" data-testid="admin-cookbook-edit-notify" checked={form.notify} onChange={(e) => set("notify", e.target.checked)} className="w-4 h-4" />
              Email the owner about these changes
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} data-testid="admin-cookbook-edit-cancel" className="btn-outline-ink !py-2 !px-4 text-sm">Cancel</button>
              <button type="button" onClick={save} disabled={busy || uploading} data-testid="admin-cookbook-edit-save" className="btn-amber !py-2 !px-4 text-sm">{busy ? "Saving…" : "Save changes"}</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
